import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import Page from 'assets/components/page/Page';
import ThemePageStatic1 from 'assets/components/page/themes/Theme.Page.Static.1';
import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { ascInsuranceLogo, ascLogoWhite } from 'assets/themes/assets/Images';
import Theme from 'assets/themes/Theme.Common';
import { generateYoutubeThumbnailUrl } from 'assets/utils/helpers/UrlHelper';
import parseJwt from 'assets/utils/parsersAndValidation/Parsers';
import { HttpStatus, publicHomeRoute } from 'config/Api.Config';
import { getAuthToken } from 'config/Auth.Config';
import { isEmpty } from 'lodash';
import ContractConsentRequestType from 'models/enums/ContractConsentRequestType';
import HashedStringStore from 'models/hashedStringStore/Model.HashedStringStore';
import publicProcessContractConsentApi from 'models/public/processContractConsent/Api.ProcessContractConsent';
import ContractConsent from 'models/sales/contractConsent/Model.ContractConsent';
import { useMemo, useState } from 'react';

const customButtonStyles: React.CSSProperties = {
  padding: '10px 20px',
  margin: '5px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: 700,
  color: '#fff',
};

const flexRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const flexColumn: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

export const confirmContractConsentRoute = publicHomeRoute + '/confirm/contractConsent/:hs?';

const ConfirmContractConsentIndex = () => {
  const { lang } = getLocales();
  const authToken = getAuthToken();
  const { query } = usePageRouter<undefined, { hs: string }>({
    route: confirmContractConsentRoute,
  });

  const { payload: hashedStringStore, isExecuting: isFetchingHashedString } = useApi(
    {
      action: HashedStringStore.getHashedString,
      body: {
        hash: decodeURIComponent(query?.hs),
        access_token: authToken,
      },
      wait: !authToken,
    },
    [authToken]
  );

  const accessToken = useMemo(() => hashedStringStore?.originalValue, [hashedStringStore?.originalValue]);
  const tokenUnpacked = useMemo(() => {
    if (accessToken)
      return parseJwt<Api.TokenUnpacked & { ContractId?: number; ContractConsentId: number }>(accessToken);
  }, [accessToken]);

  const { payload: contractConsent, isExecuting } = useApi(
    {
      action: ContractConsent.findPublic,
      body: {
        params: { contractConsentId: tokenUnpacked?.ContractConsentId },
        access_token: accessToken,
      },
      wait: !accessToken,
    },
    [accessToken]
  );
  const { payload: contractConsentDocuments, isExecuting: isFetchingDocs } = useApi(
    {
      action: ContractConsent.getDocumentsPublic,
      body: {
        params: { contractConsentId: tokenUnpacked?.ContractConsentId },
        access_token: accessToken,
      },
      wait: !accessToken,
    },
    [accessToken]
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>(undefined);

  const processConsent = async ({ isAccepted }: { isAccepted: boolean }) => {
    setIsLoading(true);
    const res = await publicProcessContractConsentApi({
      body: {
        contractConsentId: tokenUnpacked.ContractConsentId,
        contractId: tokenUnpacked.ContractId,
        requestType: ContractConsentRequestType.TEXT,
        isAccepted,
      },
      access_token: accessToken,
    });

    if (res.status === HttpStatus.ok) setMessage(lang.thankYouForYourResponse);
    else if (res.status === HttpStatus.unauthorized) setMessage(lang.consentResponseWindowExpired);
    else setMessage(lang.youAlreadySubmittedYourResponseForThisConsent);
    setIsLoading(false);
  };

  const isTaxValid = useMemo(() => {
    const tax = contractConsent?.product?.productTaxes?.find(
      (it) => it.provinceCode === contractConsent?.dealer?.provinceCode
    );
    if (tax && (tax.dealerCostGstHst > 0 || tax.dealerCostProvincialSalesTax > 0)) return true;
    return false;
  }, [contractConsent]);

  return (
    <Page
      class={ThemePageStatic1}
      isLoading={isFetchingHashedString || isExecuting || isFetchingDocs || isLoading}
      header={
        <Card
          class={ThemeCardRow}
          media={<img style={{ objectFit: 'contain' }} src={ascLogoWhite} alt="ASC Logo" />}
          style={{
            card: { gap: Theme.Size.M },
            cardMedia: { height: 'auto', width: '400px', objectFit: 'contain' },
            cardTitle: { textAlign: 'center' },
          }}
        />
      }
      style={{
        pageContent: {
          padding: 0,
        },
        backgroundColor: '#f8f8f8',
        fontFamily: 'Arial, Verdana, Geneva, Tahoma, sans-serif',
        fontSize: '14px',
      }}
      render={
        !message && !isLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: Theme.Size.M,
              maxWidth: '800px',
              margin: '20px auto',
              padding: '25px',
              backgroundColor: '#fff',
            }}
          >
            <div
              style={{
                maxWidth: '800px',
                margin: '5px auto',
                padding: '25px',
                backgroundColor: '#fff',
                border: '5px solid #f6893d',
                boxShadow: '0 0 0 5px #24408a',
                ...flexColumn,
                gap: '20px',
              }}
            >
              <div
                style={{
                  ...flexRow,
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  justifyContent: 'space-evenly',
                  gap: '40px',
                }}
              >
                <img height="80" src={ascInsuranceLogo} alt="Autoshield Canada" />
                <Button
                  style={{
                    ...customButtonStyles,
                    backgroundColor: '#28a745',
                    padding: '20px',
                    width: '175px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                  isLoading={isLoading}
                  onClick={async () => await processConsent({ isAccepted: true })}
                  render={lang.acceptOffer}
                />
              </div>
              <div>
                <hr />
                <h2 style={{ textAlign: 'center', color: '#24408a' }}>{lang.customerAcceptance}</h2>
                <hr />
              </div>
              <div style={{ ...flexColumn, gap: '10px' }}>
                <span style={{ fontStyle: 'italic' }}>Dear {contractConsent?.customerName},</span>
                <span style={{ fontStyle: 'italic' }}>
                  Thank you for your interest in protecting your vehicle with {contractConsent?.programName} Protection
                  Plan, provided by ASC Insurance Services and insured by Tokio Marine Canada Ltd.
                </span>
                <span style={{ fontStyle: 'italic' }}>
                  <strong>
                    Please take a moment to watch the video below and see how this Road Hazard Insurance program can
                    help protect your vehicle.
                  </strong>
                  You&apos;ll learn about the valuable benefits that keep you driving with confidence.
                </span>
                <span style={{ fontStyle: 'italic' }}>
                  {lang.video}:{' '}
                  <a href={contractConsent?.product?.consentVideoLink} style={{ color: '#24408a' }}>
                    RV Shield Insurance | ASC Insurance Services
                  </a>
                </span>
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                  <a href={contractConsent?.product?.consentVideoLink} target="_blank" rel="noreferrer">
                    <img
                      src={generateYoutubeThumbnailUrl(contractConsent?.product?.consentVideoLink)}
                      alt="Watch Video"
                      style={{ maxWidth: '100%', height: 'auto', borderRadius: '5px' }}
                    />
                  </a>
                </div>
              </div>
              <div style={{ ...flexColumn, margin: '50px 25px', marginBottom: 0 }}>
                <div style={flexRow}>
                  <strong>{lang.coverage}</strong>
                  <span style={{ fontStyle: 'italic' }}>{contractConsent?.coverage}</span>
                </div>

                {!isEmpty(contractConsent?.contractConsentEnhancements) && (
                  <div style={flexRow}>
                    <strong>{lang.enhancements}</strong>
                    <span style={{ fontStyle: 'italic' }}>
                      {contractConsent?.contractConsentEnhancements
                        ?.map((it) => it.productEnhancement?.name)
                        ?.join(', ')}
                    </span>
                  </div>
                )}

                {!isEmpty(contractConsent?.subfees) && (
                  <div style={flexRow}>
                    <strong>{lang.surcharges}</strong>
                    <span style={{ fontStyle: 'italic' }}>
                      {contractConsent?.subfees?.map((it) => it?.name)?.join(', ')}
                    </span>
                  </div>
                )}

                <div style={flexRow}>
                  <strong>{lang.term}</strong>
                  <span style={{ fontStyle: 'italic' }}>
                    {contractConsent?.term} {lang.months}
                  </span>
                </div>

                <div style={flexRow}>
                  <strong>{lang.aggregateLimitOfLiability}</strong>
                  <span style={{ fontStyle: 'italic' }}>{contractConsent?.aggregateDescription}</span>
                </div>

                <div style={flexRow}>
                  <strong>{lang.totalRetailPrice}</strong>
                  <span style={{ fontStyle: 'italic' }}>
                    ${contractConsent?.salePriceCAD}
                    {isTaxValid && `+ ${lang.applicable} ${lang.tax}`}
                  </span>
                </div>

                <div style={flexRow}>
                  <strong>{lang.referringDealer}</strong>
                  <span style={{ fontStyle: 'italic' }}>{contractConsent?.dealership}</span>
                </div>

                <div style={flexRow}>
                  <strong>{lang.insurancePremium}</strong>
                  <span style={{ fontStyle: 'italic' }}>${contractConsent?.insurancePremiumCAD}</span>
                </div>

                <div style={flexRow}>
                  <strong>{lang.dealerReferralFee}</strong>
                  <span style={{ fontStyle: 'italic' }}>${contractConsent?.administrationFeeCAD} + Tax</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '25px', margin: '20px 0' }}>
                <Button
                  style={{ ...customButtonStyles, backgroundColor: '#28a745' }}
                  onClick={async () => await processConsent({ isAccepted: true })}
                  isLoading={isLoading}
                  render={lang.accept}
                />
                <Button
                  style={{ ...customButtonStyles, backgroundColor: '#dc3545' }}
                  onClick={async () => await processConsent({ isAccepted: false })}
                  isLoading={isLoading}
                  render={lang.decline}
                />
              </div>
              <div>
                <p style={{ fontStyle: 'italic' }}>
                  We appreciate your business and look forward to supporting you on the road ahead.
                </p>
              </div>
              <div style={{ textAlign: 'left', fontStyle: 'italic', marginTop: '20px' }}>
                <p>
                  <strong>ASC Insurance Services Team</strong>
                </p>
                <p>
                  <strong>{lang.licenseNumber}:</strong> LIC-202074C149374R03
                </p>
                <p>
                  <strong>{lang.email}:</strong>
                  <a href="mailto:customerconcierge@asc-insurance.com" style={{ color: '#24408a' }}>
                    customerconcierge@asc-insurance.com
                  </a>
                </p>
                <p>
                  <strong>{lang.phoneNumber}:</strong> 1-888-300-2050
                </p>
                <p style={{ margin: '5px 0', fontSize: '10px', color: '#24408a' }}>
                  ASC Insurance Services is Operated by Ai Insurance Organization Inc.
                </p>
                <p style={{ margin: '5px 0', fontSize: '10px' }}>
                  <span style={{ color: '#f6893d' }}>North Service Rd. W, Suite 605,</span>
                </p>
                <p style={{ margin: '5px 0', fontSize: '10px', color: '#f6893d' }}>Oakville, ON, L6M 2W2</p>
              </div>
            </div>
            <ContractConsentDocumentPreview documents={contractConsentDocuments} />
          </div>
        ) : (
          <div
            style={{
              ...flexColumn,
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '50px',
            }}
          >
            <div style={{ ...flexColumn, alignItems: 'center', gap: '20px' }}>
              <img width={400} src={ascInsuranceLogo} alt="ASC Insurance" />
              <p style={{ fontSize: Theme.FontSize.L }}>{lang.welcomeToAutoshieldCanada}!</p>
            </div>
            <div>
              <p>Dear customer,</p>
              <p style={{ color: Theme.Color.success }}>{message}</p>
            </div>
          </div>
        )
      }
    />
  );
};

function ContractConsentDocumentPreview({ documents }: { documents: Model.IMediaFile[] }) {
  const { lang } = getLocales();

  return (
    <>
      {!documents?.length ? (
        <ErrorWrapper children={lang.noFiles} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4>{lang.supportingDocumentation}</h4>
          <table className={ThemeTableSimple2}>
            <thead>
              <tr>
                <th>{lang.fileName}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={documents}
                render={({ data: doc, index }) => (
                  <tr key={doc.id ?? `index-${index}`}>
                    <td>{doc.name}</td>
                    <td>
                      <Button
                        class={ThemeButtonCircle}
                        url={doc?.url}
                        media={<Icon class="fas-download" />}
                        target="_blank"
                        disabled={!doc.id || !doc.url}
                      />
                    </td>
                  </tr>
                )}
              />
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default ConfirmContractConsentIndex;
